import { Routes } from '@angular/router';
import { AuthGuard } from '@guards/auth.guard';
import { TrustedGuard } from '@guards/trusted.guard';
import { PendingChangesGuard } from '@guards/pending-changes.guard';
import { featureFlagGuard } from '@guards/feature-flag.guard';
import { LaunchDarklyFlags } from '@models/feature-flag.enum';

// It feels dirty to do this, so I separated modal routes from the rest.
// in the future, we should be able to use the modal service provided to us
// by something like ng-bootstrap, so these would not be necessary.
const modalRoutes: Routes = [
  {
    path: 'copyErrors',
    loadComponent: () =>
      import('./components/tool-copy-errors/tool-copy-errors.component').then(
        (m) => m.ToolCopyErrorsComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'workshopAccounts',
    loadComponent: () =>
      import('./components/workshop-accounts/workshop-accounts.component').then(
        (m) => m.WorkshopAccountsComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'workshopAccountSetup',
    loadComponent: () =>
      import(
        './components/workshop-account-setup/workshop-account-setup.component'
      ).then((m) => m.WorkshopAccountSetupComponent),
    outlet: 'modal',
  },
  {
    path: 'workshopPasswords',
    loadComponent: () =>
      import(
        './components/workshop-passwords/workshop-passwords.component'
      ).then((m) => m.WorkshopPasswordsComponent),
    outlet: 'modal',
  },
  {
    path: 'assignCSM',
    loadComponent: () =>
      import('./components/assign-csm/assign-csm.component').then(
        (m) => m.AssignCsmComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'confirmserious',
    loadComponent: () =>
      import('./components/confirmserious/confirmserious.component').then(
        (m) => m.ConfirmseriousComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'subscriptionInfo',
    loadComponent: () =>
      import('./components/subscription-info/subscription-info.component').then(
        (m) => m.SubscriptionInfoComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'subscription',
    loadComponent: () =>
      import('./components/subscription/subscription.component').then(
        (m) => m.SubscriptionComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'coupon',
    loadComponent: () =>
      import('./components/coupon/coupon.component').then(
        (m) => m.CouponComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'subscriptionSync',
    loadComponent: () =>
      import('./components/subscription-sync/subscription-sync.component').then(
        (m) => m.SubscriptionSyncComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'confirm-dialog',
    loadComponent: () =>
      import('./components/confirm-dialog/confirm-dialog.component').then(
        (m) => m.ConfirmDialogComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'user',
    loadComponent: () =>
      import('./components/user/user.component').then((m) => m.UserComponent),
    outlet: 'modal',
  },
  {
    path: 'transfer-budget',
    loadComponent: () =>
      import('./components/transfer-budget/transfer-budget.component').then(
        (m) => m.TransferBudgetComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'transfer-account',
    loadComponent: () =>
      import('./components/transfer-account/transfer-account.component').then(
        (m) => m.TransferAccountComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'add-admin',
    loadComponent: () =>
      import('./components/add-admin-dialog/add-admin-dialog.component').then(
        (m) => m.AddAdminDialogComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'greeniustoken',
    loadComponent: () =>
      import('./components/greeniustoken/greeniustoken.component').then(
        (m) => m.GreeniusTokenComponent,
      ),
    outlet: 'modal',
  },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/default/default.component').then(
        (m) => m.DefaultComponent,
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./components/default/default.component').then(
        (m) => m.DefaultComponent,
      ),
    title: 'Login',
  },
  {
    path: 'accountsnew',
    loadComponent: () =>
      import('./components/accounts-new/accounts-new.component').then(
        (m) => m.AccountsNewComponent,
      ),
    title: 'Accounts New',
    canActivate: [AuthGuard],
  },
  {
    path: 'accounts/strategic',
    loadComponent: () =>
      import('./components/account-strategic/account-strategic.component').then(
        (m) => m.AccountStrategicComponent,
      ),
    title: 'Create Strategic Account',
    canActivate: [AuthGuard],
  },
  {
    path: 'transfer',
    loadComponent: () =>
      import('./components/account-transfer/account-transfer.component').then(
        (m) => m.AccountTransferComponent,
      ),
    title: 'Transfer Data',
    canActivate: [AuthGuard],
  },
  {
    path: 'accounts/manual',
    loadComponent: () =>
      import(
        './components/account-manual-setup/account-manual-setup.component'
      ).then((m) => m.AccountManualSetupComponent),
    title: 'Create Manual Account',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'accounts/:id',
    loadComponent: () =>
      import('./components/account-detail/account-detail.component').then(
        (m) => m.AccountDetailComponent,
      ),
    title: 'Account Detail',
    canActivate: [AuthGuard],
  },
  {
    path: 'home',
    loadComponent: () =>
      import('./components/home/home.component').then((m) => m.HomeComponent),
    title: 'Home',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/accounts',
    loadComponent: () =>
      import('./components/report-accounts/report-accounts.component').then(
        (m) => m.ReportAccountsComponent,
      ),
    title: 'Reports: Accounts',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/referrals',
    loadComponent: () =>
      import('./components/report-referrals/report-referrals.component').then(
        (m) => m.ReportReferralsComponent,
      ),
    title: 'Reports: Referrals',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/csm-review',
    loadComponent: () =>
      import(
        './components/report-csm-reviews/report-csm-reviews.component'
      ).then((m) => m.ReportCsmReviewsComponent),
    title: 'Reports: CSM Review',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/email',
    loadComponent: () =>
      import('./components/report-emails/report-emails.component').then(
        (m) => m.ReportEmailsComponent,
      ),
    title: 'Reports: Email',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/survey',
    loadComponent: () =>
      import(
        './components/report-accounts-survey/report-accounts-survey.component'
      ).then((m) => m.ReportAccountsSurveyComponent),
    title: 'Reports: Survey',
    canActivate: [AuthGuard],
  },
  {
    path: 'reports/cancellation',
    loadComponent: () =>
      import(
        './components/report-cancellations/report-cancellations.component'
      ).then((m) => m.ReportCancellationsComponent),
    title: 'Reports: Cancellation',
    canActivate: [AuthGuard],
  },
  {
    path: 'organization/setup',
    loadComponent: () =>
      import(
        './components/organization-setup/organization-setup.component'
      ).then((m) => m.OrganizationSetupComponent),
    title: 'Organization Setup',
    canActivate: [AuthGuard],
  },
  {
    path: 'organization/LMNAccount',
    loadComponent: () =>
      import('./components/lmn-account/lmn-account.component').then(
        (m) => m.LmnAccountComponent,
      ),
    title: 'LMN Account',
    canActivate: [AuthGuard],
  },
  {
    path: 'organization/LMNAccount/:organizationID',
    loadComponent: () =>
      import('./components/lmn-account/lmn-account.component').then(
        (m) => m.LmnAccountComponent,
      ),
    title: 'LMN Account',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'organization/groundsguyssync',
    loadComponent: () =>
      import(
        './components/organization-groundsguys-sync/organization-groundsguys-sync.component'
      ).then((m) => m.OrganizationGroundsguysSyncComponent),
    title: 'Ground Guys Sync',
    canActivate: [AuthGuard],
  },
  {
    path: 'configurations/configuration',
    loadComponent: () =>
      import('./components/configuration/configuration.component').then(
        (m) => m.ConfigurationComponent,
      ),
    title: 'Configuration',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'configurations/registrationPromotion',
    loadComponent: () =>
      import(
        './components/registration-promotion/registration-promotion.component'
      ).then((m) => m.RegistrationPromotionComponent),
    title: 'Registration Promotion',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'configurations/registrationPromotion/:id',
    loadComponent: () =>
      import(
        './components/registration-promotion-detail/registration-promotion-detail.component'
      ).then((m) => m.RegistrationPromotionDetailComponent),
    title: 'Registration Promotion Detail',
    canActivate: [AuthGuard, TrustedGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'news',
    loadComponent: () =>
      import('./components/news/news.component').then((m) => m.NewsComponent),
    title: 'News',
    canActivate: [AuthGuard],
  },
  {
    path: 'franchises/franchiseGroup',
    loadComponent: () =>
      import('./components/franchise-group/franchise-group.component').then(
        (m) => m.FranchiseGroupComponent,
      ),
    title: 'Franchise Group',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'franchises/franchiseList',
    loadComponent: () =>
      import('./components/franchise-list/franchise-list.component').then(
        (m) => m.FranchiseListComponent,
      ),
    title: 'Franchise List',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'multibetatestaccount',
    loadComponent: () =>
      import(
        './components/multi-beta-test-account/multi-beta-test-account.component'
      ).then((m) => m.MultiBetaTestAccountComponent),
    title: 'Beta Test Accounts',
    canActivate: [AuthGuard],
  },
  {
    path: 'tools/copyestimates',
    loadComponent: () =>
      import(
        './components/tool-copy-estimates/tool-copy-estimates.component'
      ).then((m) => m.ToolCopyEstimatesComponent),
    title: 'Tools: Copy Estimates',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'tools/portal-user-purge',
    loadComponent: () =>
      import(
        './components/tool-portal-user-purge/tool-portal-user-purge.component'
      ).then((m) => m.ToolPortalUserPurge),
    title: 'Tools: Portal User Purge',
    canActivate: [AuthGuard],
  },
  {
    path: 'tools/lmn-user-unlink',
    loadComponent: () =>
      import(
        './components/tool-lmn-user-unlink/tool-lmn-user-unlink.component'
      ).then((m) => m.ToolLmnUserUnlink),
    title: 'Tools: LMN User Unlink',
    canActivate: [AuthGuard],
  },
  {
    path: 'tools/siteone-materials',
    loadComponent: () =>
      import(
        './components/tool-siteone-materials/tool-siteone-materials.component'
      ).then((m) => m.ToolSiteoneMaterialsComponent),
    title: 'Tools: SiteOne Materials',
    canActivate: [AuthGuard, TrustedGuard],
  },
  // {
  //   path: 'users',
  //   loadComponent: () =>
  //     import('./components/users/users.component').then(
  //       (m) => m.UsersComponent
  //     ),
  //   canActivate: [AuthGuard],
  // },
  // {
  //   path: 'users/:id',
  //   loadComponent: () =>
  //     import('./components/user-details/user-details.component').then(
  //       (m) => m.UserDetailsComponent
  //     ),
  //   canActivate: [AuthGuard],
  // },
  {
    path: 'dashboards/multidashboards',
    loadComponent: () =>
      import('./components/dashboards/dashboards.component').then(
        (m) => m.DashboardsComponent,
      ),
    title: 'Analytic List: Dashboards',
    canActivate: [AuthGuard, TrustedGuard],
  },
  {
    path: 'admins',
    loadComponent: () =>
      import('./components/admin-list/admin-list.component').then(
        (m) => m.AdminListComponent,
      ),
    title: 'Admins',
    canActivate: [
      AuthGuard,
      TrustedGuard,
      featureFlagGuard(LaunchDarklyFlags.ADMIN_LIST),
    ],
  },
  {
    path: '**',
    loadComponent: () =>
      import('./components/page-not-found/page-not-found.component').then(
        (m) => m.PageNotFoundComponent,
      ),
    title: '404',
  },
  ...modalRoutes,
];
